import fs from 'node:fs';
import path from 'node:path';
import { logger } from '../utils/logger.js';
import {
  clearBootstrapState,
  getBootstrapStatePath,
} from '../utils/bootstrap-state.js';
import { resolvePlatformApiEndpoint, revokeCliAuthToken } from '../utils/cli-auth-api.js';
import {
  clearCliAuth,
  hasStoredCliAuth,
  readStoredRefreshToken,
} from '../utils/cli-auth-storage.js';
import { PROJECT_AUTH_ENV_KEYS } from '../utils/project-auth-env.js';

const ORG_CONTEXT_KEYS: ReadonlySet<string> = new Set(PROJECT_AUTH_ENV_KEYS);

const DEV_VARS_FILENAME = '.dev.vars';

interface LogoutOptions {
  org?: string;
  endpoint?: string;
}

export function stripOrgContextFromDevVars(content: string): { updated: string; removed: boolean } {
  const lines = content.split('\n');
  let removed = false;
  const kept = lines.filter((line) => {
    const eqIndex = line.indexOf('=');
    if (eqIndex <= 0) return true;
    const key = line.slice(0, eqIndex).trim();
    if (ORG_CONTEXT_KEYS.has(key)) {
      removed = true;
      return false;
    }
    return true;
  });

  return { updated: kept.join('\n'), removed };
}

export function clearLocalOrgContext(projectPath: string): { stateCleared: boolean; envCleared: boolean } {
  const statePath = getBootstrapStatePath(projectPath);
  const stateCleared = fs.existsSync(statePath);
  clearBootstrapState(projectPath);

  const devVarsPath = path.join(projectPath, DEV_VARS_FILENAME);
  if (!fs.existsSync(devVarsPath)) {
    return { stateCleared, envCleared: false };
  }

  const content = fs.readFileSync(devVarsPath, 'utf-8');
  const stripped = stripOrgContextFromDevVars(content);
  if (!stripped.removed) {
    return { stateCleared, envCleared: false };
  }

  fs.writeFileSync(devVarsPath, stripped.updated, 'utf-8');
  return { stateCleared, envCleared: true };
}

function logLocalClearResult(result: { stateCleared: boolean; envCleared: boolean }): void {
  if (!result.stateCleared && !result.envCleared) {
    logger.info('No local org-scoped session found to clear.');
    return;
  }
  logger.success('Cleared local org-scoped session state.');
}

export async function logout(options: LogoutOptions = {}): Promise<void> {
  const projectPath = process.cwd();
  const localResult = clearLocalOrgContext(projectPath);

  const storedAuth = hasStoredCliAuth();
  const tokenPayload = readStoredRefreshToken(options.org);

  if (!storedAuth && !tokenPayload) {
    logger.info('No stored CLI auth session found.');
    logLocalClearResult(localResult);
    return;
  }

  const endpoint = resolvePlatformApiEndpoint(options.endpoint);

  if (tokenPayload) {
    try {
      await revokeCliAuthToken(endpoint, tokenPayload.refreshToken);
    } catch (error) {
      if (error instanceof Error) {
        logger.warning(`Token revocation failed: ${error.message}`);
      } else {
        logger.warning('Token revocation failed');
      }
    }
  }

  if (!storedAuth) {
    logger.info('Refresh token came from STANDARDAGENTS_TOKEN. Unset it in your shell to fully logout.');
    logLocalClearResult(localResult);
    return;
  }

  const removed = clearCliAuth(options.org);
  if (!removed && options.org) {
    logger.error(`No stored auth session found for "${options.org}".`);
    process.exit(1);
  }

  if (!removed) {
    logger.info('No stored CLI auth session found.');
    logLocalClearResult(localResult);
    return;
  }

  logger.success(`Logged out from org "${removed.org_slug}" (${removed.user_email}).`);
  logLocalClearResult(localResult);
}
